//@flow

import React, { Component, Fragment } from 'react';
import head from 'lodash/head';
import times from 'lodash/times';
import isEmpty from 'lodash/isEmpty';
import { parse } from 'query-string';
import { camelizeKeys } from 'humps';
import { Formik, Field } from 'formik';
import { string, object } from 'yup';
import { RadioGroup, Radio } from 'react-radio-group';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import {
  Label,
  FormGroup,
  Col,
  Row,
  Button,
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';

import api from '../../../../lib/api';
import endpoints from '../../../../lib/endpoints';
import { Loading, WhitePanel } from '../../../../components';
import NationalityDropdown from './NationalityDropdown';
import PassengerDetailsDataBuilder from './PassengerDetailsDataBuilder';

import './passengerdetail.css';
import { appendHoneyGuide } from '../../../../lib/utils';
type Props = {
  match: {
    params: {
      invoiceId: string,
    },
  },
};
type State = RouteComponentProps & {
  pax: null | number,
  flightId: null | string,
  returnFlightId: null | string,
  formError: null | string,
  contactPersonFormError: null | string,
  agreeToTermsAndConditions: null | boolean,
  termsAndConditionsError: null | string,
  lineItemId: null | string,
  flightsData: null | Object,
  loading: boolean,
  sessionId: string,
  reservationError: boolean,
};

const makeErrorMessageFor = str => `Please enter your ${str}`;

const FormSchema = object().shape({
  firstName: string().required(makeErrorMessageFor('first name')),
  lastName: string().required(makeErrorMessageFor('last name')),
  title: string().required('Please select one'),
  nationality: string().required('Please select one'),
});

const emailAddressErrorMessage =
  'Please enter your email address in format: yourname@example.com';

const ContactPersonSchema = object().shape({
  firstName: string().required(makeErrorMessageFor('first name')),
  lastName: string().required(makeErrorMessageFor('last name')),
  contactEmail: string()
    .email(emailAddressErrorMessage)
    .required(emailAddressErrorMessage),
  contactMobile: string().required(makeErrorMessageFor('contact number')),
});

class CustomerDetail extends Component<Props, State> {
  state = {
    pax: null,
    flightId: null,
    returnFlightId: null,
    lineItemId: null,
    formError: null,
    contactPersonFormError: null,
    agreeToTermsAndConditions: false,
    termsAndConditionsError: null,
    flightsData: null,
    loading: false,
    reservationError: false,
  };

  async componentDidMount() {
    await this.fetchInvoice();
    await this.reserveFlights();
  }

  async fetchInvoice() {
    const { params } = this.props.match;

    const { data: invoices } = await api.get(
      endpoints.payment.getInvoice(params.invoiceId)
    );

    const { itemData, id } = camelizeKeys(
      head(invoices.line_items.filter(l => l.item_type === 'flight'))
    );

    const { trek_title, pax } = invoices.invoice.trek;

    document.title = appendHoneyGuide(`Travelers Details | ${trek_title}`);

    // const travelerDetailsAlreadyExistsInInvoice = Boolean(itemData.contactName);

    this.setState({
      pax: Number(pax),
      flightId: itemData.flightId,
      returnFlightId: itemData.returnFlightId,
      lineItemId: id,
      flightsData: itemData,
      // if the invoice already contains the traveler details.
      // that means the user has already agreed to terms and conditions before.
      // because without agreeing to terms and conditions
      // the user can't send their details to the invoice.
      //! bug at how the checkbox onclick is handled
      // agreeToTermsAndConditions: travelerDetailsAlreadyExistsInInvoice,
    });
  }

  async reserveFlights() {
    const { session_id } = parse(this.props.location.search);
    const { flightId, returnFlightId } = this.state;

    if (!flightId) return;

    const requestObj = {
      flight_id: flightId,
      session_id,
      ...(returnFlightId ? { return_flight_id: returnFlightId } : {}),
    };

    try {
      await api.postWithoutToken('flights/reservation', requestObj);
    } catch (err) {
      this.setState(() => ({ reservationError: true }));
    }
  }

  handleAgreement = () =>
    this.setState({
      agreeToTermsAndConditions: !this.state.agreeToTermsAndConditions,
      termsAndConditionsError: '',
    });

  render() {
    const {
      pax,
      contactPersonFormError,
      agreeToTermsAndConditions,
      termsAndConditionsError,
      flightsData,
      reservationError,
    } = this.state;

    if (!pax) return <Loading />;

    return (
      <Fragment>
        <ReservationFailedModal isOpen={reservationError} history={this.props.history} />
        <Container>
          <WhitePanel>
            <h1>CUSTOMER DETAILS</h1>
            <p>
              Please enter the following details as it appears in your Passport or Official
              Travel Document.
            </p>
            <div
              className="white-panel"
              ref={node => {
                this.contactPersonDomNode = node;
              }}
            >
              <h2>CONTACT PERSON</h2>
              <Row className="contact-persion">
                <Formik
                  validateOnBlur
                  initialValues={{
                    firstName:
                      (flightsData &&
                        flightsData.contactName &&
                        flightsData.contactName.split(' ')[0]) ||
                      '',
                    lastName:
                      (flightsData &&
                        flightsData.contactName &&
                        flightsData.contactName.split(' ')[1]) ||
                      '',
                    contactMobile: (flightsData && flightsData.contactMobile) || '',
                    contactEmail: (flightsData && flightsData.contactEmail) || '',
                  }}
                  validationSchema={ContactPersonSchema}
                  ref={node => {
                    this.contactPerson = node;
                  }}
                >
                  {({ errors, touched }) => (
                    <Fragment>
                      <ColField
                        label="First Name"
                        name="firstName"
                        placeholder="Enter your first name"
                        errors={errors}
                        touched={touched}
                      />
                      <ColField
                        label="Last Name"
                        name="lastName"
                        placeholder="Enter your last name"
                        errors={errors}
                        touched={touched}
                      />
                      <ColField
                        label="Email"
                        placeholder="yourname@example.com"
                        name="contactEmail"
                        errors={errors}
                        touched={touched}
                        smallLabel="(Trip Details will be sent to this email)"
                      />
                      <ColField
                        label="Contact Number"
                        type="tel"
                        placeholder="+1-2025550107"
                        name="contactMobile"
                        errors={errors}
                        touched={touched}
                      />
                    </Fragment>
                  )}
                </Formik>
                <ErrorMessage
                  showError={Boolean(contactPersonFormError)}
                  message={contactPersonFormError}
                />
              </Row>
            </div>

            <div className="white-panel">
              <h2>TRAVELERS DETAILS</h2>
              {times(this.state.pax).map((e, i) => (
                <Fragment key={i}>
                  <div
                    className="row pssenger-detail-wrap"
                    ref={node => {
                      this[`passengerDomNode${i}`] = node;
                    }}
                  >
                    <Formik
                      initialValues={{
                        firstName:
                          (flightsData &&
                            flightsData.passengerDetail &&
                            flightsData.passengerDetail[i].firstName) ||
                          '',
                        lastName:
                          (flightsData &&
                            flightsData.passengerDetail &&
                            flightsData.passengerDetail[i].lastName) ||
                          '',
                        title:
                          (flightsData &&
                            flightsData.passengerDetail &&
                            flightsData.passengerDetail[i].title) ||
                          '',
                        nationality:
                          (flightsData &&
                            flightsData.passengerDetail &&
                            flightsData.passengerDetail[i].nationality) ||
                          '',
                      }}
                      validateOnBlur
                      validationSchema={FormSchema}
                      ref={node => {
                        this[`passenger${i}`] = node;
                      }}
                    >
                      {({ errors, touched, values, setFieldValue }) => (
                        <Fragment key={i}>
                          <ColField
                            label="First Name"
                            placeholder="Enter First Name"
                            name="firstName"
                            errors={errors}
                            touched={touched}
                          />
                          <ColField
                            label="Last Name"
                            placeholder="Enter Last Name"
                            name="lastName"
                            errors={errors}
                            touched={touched}
                          />

                          <Col lg="3" md="4">
                            <FormGroup>
                              <Label>Nationality</Label>
                              <Field
                                name="nationality"
                                value={values.nationality}
                                setFormikState={v => setFieldValue('nationality', v)}
                                component={NationalityDropdown}
                              />
                              <ErrorMessage
                                showError={errors.nationality && touched.nationality}
                                message={errors.nationality}
                              />
                            </FormGroup>
                          </Col>
                          <Col lg="3" md="4">
                            <FormGroup>
                              <Label>Gender</Label>
                              <RadioGroup
                                name={`title${i}`}
                                selectedValue={values.title}
                                className="row radio-fix"
                                onChange={v => setFieldValue('title', v)}
                              >
                                <Label className="col-6 passenger-radio">
                                  <Radio value="Mr" className="flight" /> <span /> Male
                                </Label>
                                <Label className="col-6 passenger-radio">
                                  <Radio value="Ms" className="flight" /> <span /> Female
                                </Label>
                              </RadioGroup>
                              <ErrorMessage
                                showError={errors.title && touched.title}
                                message={errors.title}
                              />
                            </FormGroup>
                          </Col>
                        </Fragment>
                      )}
                    </Formik>
                  </div>
                  <hr />
                </Fragment>
              ))}
            </div>

            <Row className="my-2">
              <Col className="terms-conditions">
                <p>
                  <label>
                    <input
                      type="checkbox"
                      onChange={this.handleAgreement}
                      value={agreeToTermsAndConditions}
                    />
                    <span /> By clicking on the continue button you have our guarantee and
                    agree with our{' '}
                    <a
                      href="/policies/terms-services"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#4bc9f7' }}
                    >
                      terms of services
                    </a>{' '}
                    . Thank you for trusting our sevices.
                  </label>
                  {termsAndConditionsError && (
                    <p className="error">{termsAndConditionsError}</p>
                  )}
                </p>
              </Col>
              <Col className="col-12 text-right">
                <Button
                  color="yellow"
                  onClick={this.onContinue}
                  className={this.state.loading ? 'btn-loading' : ''}
                >
                  {this.state.loading ? 'CONTINUING....' : 'CONTINUE'}
                </Button>
                <p className="error">{this.state.formError}</p>
              </Col>
              <br />
            </Row>
          </WhitePanel>
        </Container>
      </Fragment>
    );
  }

  onValidate(formikState) {
    const { errors, values } = formikState;
    let isError = false;

    // iterating over errors to check for errors
    if (!isEmpty(errors)) isError = true;

    // iterating over state to check for blank values
    for (let key in values) {
      if (!values[key]) isError = true;
      break;
    }

    return isError;
  }

  validatePassengerDetails() {
    let isError = false,
      errorIndex = null;

    for (let i = 0; i < this.state.pax; i++) {
      isError = this.onValidate(this[`passenger${i}`].state);
      if (isError) {
        errorIndex = i;
        break;
      }
    }

    return { isError, errorIndex };
  }

  async submitToApi() {
    const { pax, lineItemId } = this.state;
    this.setState(() => ({ loading: true }));
    const data = new PassengerDetailsDataBuilder(pax)
      .flightsIds(this.state)
      .contactPerson(this.contactPerson.state.values)
      .passengerDetail(this)
      .build();

    const {
      location: { pathname },
      history,
      match: {
        params: { invoiceId },
      },
    } = this.props;

    const url = `payments/invoices/${invoiceId}/lineitems/${lineItemId}`;
    try {
      await api.patchWithoutToken(url, data);
      const baseUrl = pathname
        .split('/')
        .slice(0, 3)
        .join('/');

      this.setState(() => ({ loading: false }));
      history.push(`${baseUrl}/payment/${invoiceId}`);
    } catch (err) {
      const invoiceAlreadyClosed = err.response.status === 400;
      if (invoiceAlreadyClosed)
        this.setState(() => ({
          termsAndConditionsError:
            'This trek has already been booked. Please check your email for trek details',
          loading: false,
        }));
    }
  }

  onContinue = async () => {
    if (!this.state.agreeToTermsAndConditions) {
      this.setState(() => ({
        agreeToTermsAndConditions: false,
        termsAndConditionsError: 'Please accept the terms and conditions to proceed.',
      }));

      return;
    }

    const errorsInContactPersonInfo = this.onValidate(this.contactPerson.state);
    const {
      isError: errorsInPassengerDetail,
      errorIndex,
    } = this.validatePassengerDetails();

    if (errorsInContactPersonInfo || errorsInPassengerDetail) {
      if (errorsInContactPersonInfo) {
        this.setState(() => ({
          contactPersonFormError:
            'There seems to be error in Contact Person Form. Please fill accordingly',
        }));

        this.contactPersonDomNode.scrollIntoView({
          behavior: 'smooth',
        });
      }

      if (errorsInPassengerDetail)
        this[`passengerDomNode${errorIndex}`].scrollIntoView({ behavior: 'smooth' });
    } else {
      this.submitToApi();
    }
  };
}

type ErrorMessageProps = {
  showError: boolean,
  message: string,
};

const ErrorMessage = ({ showError, message }: ErrorMessageProps) =>
  showError ? <p className="error">{message}</p> : null;

type ColFieldProps = {
  label: string,
  placeholder?: string,
  errors: Object,
  touched: Object,
  name: string,
  type?: string,
  lg?: string,
  md?: string,
  smallLabel?: string,
};

const ColField = ({
  label,
  placeholder = '',
  errors,
  touched,
  name,
  smallLabel,
  type = 'text',
  lg = '3',
  md = '3',
}: ColFieldProps) => (
  <Col lg={lg} md={md}>
    <FormGroup>
      <Label>
        {label} {smallLabel ? <small>{smallLabel}</small> : null}
      </Label>
      <Field type={type} placeholder={placeholder} name={name} className="form-control" />
      <ErrorMessage showError={errors[name] && touched[name]} message={errors[name]} />
    </FormGroup>
  </Col>
);

const ReservationFailedModal = ({ isOpen, history }) => (
  <Modal isOpen={isOpen}>
    <ModalHeader>Flight Reservation Failed</ModalHeader>
    <ModalBody>
      Sorry for the inconvenience. Please go back and search flights for another date.
    </ModalBody>
    <ModalFooter>
      <Button color="yellow" onClick={() => history.goBack()}>
        Go Back
      </Button>
    </ModalFooter>
  </Modal>
);

export default withRouter(CustomerDetail);



// WEBPACK FOOTER //
// ./src/screens/Trek/Self-Guided/CustomerDetails/CustomerDetails.js